import { Component, NgZone, OnDestroy } from '@angular/core';
import { AuthService } from '../core/auth-service';
import { CheckInService } from '../checkIn.service';
import { SharedService } from '../core/shared.service';
import { NotificationService } from '../services/notification.service';
import { CheckInModel } from '../models/checkIn.model';
import { Location } from '@angular/common';
import { Observable, take } from 'rxjs';
import { LoggingService } from '../services/logging.service';
import { LocationService } from '../services/location.service';

@Component({
  selector: 'app-branch-check-in',
  templateUrl: './branch-check-in.component.html',
  styleUrl: './branch-check-in.component.scss'
})
export class BranchCheckInComponent implements OnDestroy {
  constructor(
    private authService: AuthService,
    private checkInService: CheckInService,
    private sharedService: SharedService,
    private notification: NotificationService,
    private location: Location,
    private zone: NgZone,
    private loggingService: LoggingService,
    private locationService: LocationService) {
  }

  isCheckedIn: boolean;
  checkIn: boolean;
  siteId: number;
  buyerName: string;
  branchName: string;
  lat: number;
  lng: number;
  isToday: boolean;
  qrData: string;
  watchId: number | null = null;

  ngOnDestroy(): void {
    this.isCheckedIn = false;
  }

  async ngOnInit() {
    this.siteId = history?.state?.siteId;
    this.buyerName = history?.state?.buyerName;
    this.branchName = history?.state?.branchName;
    this.qrData = history?.state?.qrData;
    this.isToday = history?.state?.isToday;
    this.sharedService.reset();
    this.checkIn = history?.state?.isToday;
    this.location.replaceState('');

    const coords = await this.locationService.getGeolocation(this.buyerName);
    if (coords) {
      this.lat = coords.lat;
      this.lng = coords.long;
    }
  }

  rmCheckIn() {
    this.isCheckedIn = true;
    if (this.checkIsTodayDate()) {
      this.locationService.createPositionStream().pipe(
        take(1)
      ).subscribe(pos => {
        this.lat = pos.coords.latitude;
        this.lng = pos.coords.longitude;
        this.checkIn = false;
        this.isCheckedIn = false;
        const checkInModel = new CheckInModel();
        checkInModel.branchName = this.branchName;
        checkInModel.buyerName = this.buyerName;
        checkInModel.location = JSON.stringify({ latitude: this.lat, longitude: this.lng });
        checkInModel.siteId = this.siteId;
        checkInModel.latitude = this.lat;
        checkInModel.longitude = this.lng;
        checkInModel.selfCheckIn = false;
        this.checkInService.sendRMCheckInMessage$(checkInModel).subscribe();
      });
    } else {
      this.notification.dangerToast('Invalid QR code date')
      this.isToday = false;
    }
  }

  close() {
    this.authService.logout();
  }

  checkIsTodayDate() {
    return new Date(this.qrData).toISOString().split('T')[0] === new Date().toISOString().split('T')[0];
  }

  private createPositionStream(): Observable<GeolocationPosition> {
    return new Observable<GeolocationPosition>((observer) => {
      if (!('geolocation' in navigator)) {
        observer.error('Geolocation not supported');
        return;
      }

      // Avoid duplicate watchers
      if (this.watchId === null) {
        this.watchId = navigator.geolocation.watchPosition(
          (pos) => {
            this.zone.run(() => observer.next(pos));  // push into Angular zone
          },
          (err) => {
            this.zone.run(() => {
              switch (err.code) {
                case err.PERMISSION_DENIED:
                  this.notification.dangerToast(`You are not able to check in because you have not given permission for your location to be used. ${err.code, err.message}`);
                  break;
                case err.POSITION_UNAVAILABLE:
                  this.notification.dangerToast(`Position unavailable ${err.code, err.message}`);
                  break;
                case err.TIMEOUT:
                  this.notification.dangerToast(`Request timed out ${err.code, err.message}`);
                  break;
                default:
                  return this.notification.dangerToast("Unknown geolocation error.");
              }
            });
          },
          {
            enableHighAccuracy: false,
            timeout: 30000,
            maximumAge: 60000
          }
        );
      }

      // Cleanup
      return () => {
        if (this.watchId !== null) {
          navigator.geolocation.clearWatch(this.watchId);
          this.watchId = null;
        }
      };
    });
  }

  private getGeolocation(options = {}, timeout = 20000) {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        const errorMessage = {
          message: `Geolocation request timed out - no response from user ${this.buyerName}.`,
          stack: JSON.stringify(`Geolocation request timed out - no response from user ${this.buyerName}.`)
        };

        this.loggingService.logError(errorMessage);
      }, timeout);

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          clearTimeout(timeoutId);
          resolve(pos);
        },
        (err) => {
          clearTimeout(timeoutId);
          reject(err);
        },
        options
      );
    });
  }
}
